"use client";
import { Rabbit, Building, Layers, Banknote, ListTree, Users, Map, TableProperties, ArrowBigDown } from "lucide-react";
import SlidingLabelButton from "./SlidingLabelButton";
import { usePathname } from "@/i18n/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { ERoles } from "@/data/roles";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { AppStaticPathnames } from "@/i18n/routing";
import { ReactNode } from "react";
import { VerticalContainer } from "@/components/layout/containers/VerticalContainer";

type SideBarLink = {
  href: AppStaticPathnames;
  label: string;
  Icon: ReactNode;
};

const Sidebar = () => {
  const { user, isInRole } = useAuth();
  const pathname = usePathname();
  const t = useTranslations();

  // Define links based on roles
  const getLinksForUser = (): SideBarLink[] => {
    // OrganizationUser - full explore navigation
    if (isInRole(ERoles.OrganizationUser)) {
      return [
        { href: "/map", label: t("general.map"), Icon: <Map className="h-5 w-5 stroke-[1.33]" /> },
        { href: "/explore/companies", label: t("companies.titlePlural"), Icon: <Building className="h-5 w-5 stroke-[1.33]" /> },
        { href: "/explore/business-units", label: t("businessUnits.titlePlural"), Icon: <ListTree className="h-5 w-5 stroke-[1.33]" /> },
        { href: "/explore/categories", label: t("categories.titlePlural"), Icon: <Layers className="h-5 w-5 stroke-[1.33]" /> },
        { href: "/explore/orders", label: t("orders.title"), Icon: <TableProperties className="h-5 w-5 stroke-[1.33]" /> },
        { href: "/explore/expenses", label: t("expenses.titlePlural"), Icon: <Banknote className="h-5 w-5 stroke-[1.33]" /> },
      ];
    }

    // OrganizationAdmin - only organization management
    if (isInRole(ERoles.OrganizationAdmin)) {
      return [
        { href: "/organization/users", label: t("organizationManagement.organizationUsersTitle"), Icon: <Users className="h-5 w-5 stroke-[1.33]" /> },
        { href: "/organization/business-units", label: t("businessUnits.titlePlural"), Icon: <ListTree className="h-5 w-5 stroke-[1.33]" /> },
        { href: "/map", label: t("general.map"), Icon: <Map className="h-5 w-5 stroke-[1.33]" /> },
        { href: "/import", label: t("import.import"), Icon: <ArrowBigDown className="h-5 w-5 stroke-[1.33]" /> }
      ];
    }

    // SuperAdmin - only system management
    if (isInRole(ERoles.SuperAdmin)) {
      return [
        { href: "/manage/users", label: "Manage Users", Icon: <Users className="h-5 w-5 stroke-[1.33]" /> },
        { href: "/map", label: t("general.map"), Icon: <Map className="h-5 w-5 stroke-[1.33]" /> },
      ];
    }

    return [];
  };

  const links = getLinksForUser();
  const message = `${t("general.welcome")} ${user?.firstName || ""} ${user?.lastName || ""} (${user?.email || ""}) ${user?.currentRole?.roleName}. ${t("general.greetingsFrom", { city: "Delft" })}!`;

  return (
    <div className="relative h-full border-r bg-background px-2 py-2">
      <VerticalContainer className="items-center gap-2">
        <div className="group relative mb-2">
          <Rabbit className="h-10 w-10 cursor-pointer rounded-md bg-primary p-2 text-primary-foreground" />
          <div className="pointer-events-none absolute left-full top-0 z-[999] ml-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">{message}</div>
        </div>
        {links.map((link) => (
          <Link
            href={link.href}
            key={link.href}
          >
            <SlidingLabelButton
              Icon={link.Icon}
              label={link.label}
              active={pathname.startsWith(link.href)}
              href={link.href}
            />
          </Link>
        ))}
      </VerticalContainer>
    </div>
  );
};

export default Sidebar;
